import BaseWrapper from "../../components/ui/BaseWrapper";
import dayjs from "dayjs";
import BaseRating from "../../components/ui/BaseRating";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import { CurrencySymbol, ModuleType, PaymentType } from "../../utils/enum";
import { fetchWorkforceByTalentPool } from "../../services/api/workforce";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Avatar } from "@mui/material";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState, useMemo } from "react";
import { sortByPaymentType } from "../../utils/sortByPaymentType";
import BaseTextField from "../../components/ui/BaseTextField";
import { debounce } from "../../utils/helpers";
import WorkForceFilters from "../WorkForce/WorkForceFilters";
import { setTalentPoolFilters } from "../../store/filters/talentPoolFiltersSlice";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import { removeEngineerFromTalentPool } from "../../services/api/talentPool";
import { showToast } from "../../store/toast/toastSlice";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import useAuth from "../../hooks/useAuth";
import OpenableLink from "../../components/ui/OpenableLink";

const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const TalentPoolEngineersTable = ({ setCurrentTalentPool }) => {
  const dispatch = useDispatch();
  const { id: talentPoolId } = useParams();
  const [search, setSearch] = useState("");
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const { getPermissionForModule } = useAuth();
  const { hasDeleteAccess: hasWorkForceDeleteAccess } = getPermissionForModule(
    ModuleType.WORK_FORCE_TALENT_POOL,
  );

  const talentPoolFilters = useSelector((store) => store.talentPoolFilters.value);

  const pagination = talentPoolFilters.pagination || { page: 0, pageSize: 10 };

  const filterPayload = {
    fullName: talentPoolFilters.fullName || "",
    country: talentPoolFilters.country?.value || "",
    city: talentPoolFilters.city?.value || "",
    search: talentPoolFilters.search || "",
    skillSet: talentPoolFilters.skillSet?.value || "",
    certifications: talentPoolFilters.certifications?.value || "",
    equipment: talentPoolFilters.equipment?.value || "",
    backgroundCheck: talentPoolFilters.backgroundCheck || undefined,
    workedWith: talentPoolFilters.workedWith ?? undefined,
    jobsCompleted: talentPoolFilters.jobsCompleted ?? undefined,
    talentPool: talentPoolFilters.talentPool?.value || undefined,
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      id: talentPoolId,
    }),
    [pagination.page, pagination.pageSize, talentPoolId],
  );

  const {
    isPending: isFetchWorkforceApiLoading,
    data: talnetPoolEngineersData,
    refetch: refetchWorkforce,
  } = useQuery({
    queryKey: ["workforce", queryParams, talentPoolId, filterPayload],
    queryFn: () => fetchWorkforceByTalentPool({ ...queryParams, ...filterPayload }),
    keepPreviousData: true,
  });

  const { mutateAsync: removeUserMutation, isLoading: isRemoveLoading } = useMutation({
    mutationFn: removeEngineerFromTalentPool,
  });

  useEffect(() => {
    setCurrentTalentPool(talnetPoolEngineersData?.talentPool);
  }, [talnetPoolEngineersData]);

  const debouncedSearchRef = useRef(
    debounce((value) => {
      dispatch(setTalentPoolFilters({ filterName: "search", data: value }));
    }, 500),
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearchRef.current(e.target.value);
  };

  const appliedFilters = useMemo(() => {
    const filters = [];
    const wf = talentPoolFilters;

    if (wf?.fullName) filters.push({ prefix: "Name", value: wf.fullName, key: "fullName" });
    if (wf?.country?.label)
      filters.push({
        prefix: "Country",
        value: wf.country.label,
        key: "country",
      });
    if (wf?.city?.label) filters.push({ prefix: "City", value: wf.city.label, key: "city" });
    if (wf?.jobsCompleted)
      filters.push({
        prefix: "Jobs Completed",
        value: wf.jobsCompleted,
        key: "jobsCompleted",
      });
    if (wf?.workedWith)
      filters.push({
        prefix: "Worked With",
        value: wf.workedWith,
        key: "workedWith",
      });
    if (wf?.backgroundCheck)
      filters.push({
        prefix: "Background Check",
        value: wf.backgroundCheck,
        key: "backgroundCheck",
      });
    if (wf?.certifications?.label)
      filters.push({
        prefix: "Certification",
        value: wf.certifications.label,
        key: "certifications",
      });
    if (wf?.equipment?.label)
      filters.push({
        prefix: "Equipment",
        value: wf.equipment.label,
        key: "equipment",
      });
    if (wf?.skillSet?.label)
      filters.push({
        prefix: "Skill Set",
        value: wf.skillSet.label,
        key: "skillSet",
      });

    return filters;
  }, [talentPoolFilters]);

  const handleRemoveFilter = (key) => {
    dispatch(setTalentPoolFilters({ filterName: key, data: "" }));
    dispatch(
      setTalentPoolFilters({
        filterName: "pagination",
        data: { page: 0, pageSize: talentPoolFilters.pagination.pageSize },
      }),
    );
    setFiltersChanged(true);
  };
  const handleRemoveEngineer = async (userId) => {
    try {
      await removeUserMutation({ talentPoolId, userId });
      dispatch(
        showToast({
          message: "User removed from talent pool successfully",
          type: "success",
        }),
      );
      refetchWorkforce();
      setIsDeleteConfirmationModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to remove user",
          type: "error",
        }),
      );
    }
  };

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <OpenableLink
          as="div"
          to={`/user/details/profile/${params.row.id}`}
          className="flex items-center w-[206px]"
        >
          <p className="table-font-size font-normal text-primary cursor-pointer ml-4">
            {params.value}
          </p>
        </OpenableLink>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      renderCell: (params) => (
        <OpenableLink
          as="div"
          to={`/user/details/profile/${params.row.id}`}
          className="flex items-center cursor-pointer"
        >
          <Avatar src={params.row.profile} sx={{ width: 28, height: 28 }} />
          <p className="ml-3 table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary capitalize">
            {params.value}
          </p>
          {params.row.isFreelanceCompany && (
            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 whitespace-nowrap">
              Vendor
            </span>
          )}
        </OpenableLink>
      ),
    },
    /* {
      field: "billingRates",
      headerName: "Payment Rates",
      width: 300,
      renderCell: (params) => {
        const sortedRates = sortByPaymentType(params.value);
        return sortedRates?.map((rate) => (
          <div className="flex flex-wrap table-font-size w-full">
            <div>
              <p className="h-[14px] text-primary text-center text-[11px] font-medium">
                {rate.rate + CurrencySymbol[rate.currency] || ""}
              </p>
              <p className="text-center">{PaymentType[rate.paymentType]}</p>
            </div>
          </div>
        ));
      },
    }, */
    {
      field: "workedWithJobsCount",
      headerName: "Jobs",
      width: 160,
      renderCell: (params) => (
        <div className="table-font-size">
          {/* Marketplace vs My Company split disabled — always show My Company count.
          {params.row.workedWith ? (
            <p className="text-primary font-medium">
              {params.row.workedWithJobsCount}{" "}
              <span className="text-[11px] font-300">(My Company)</span>
            </p>
          ) : (
            <p className="font-medium">
              {params.row.marketplaceJobsCount}
              <span className="text-[11px] font-300"> (Marketplace)</span>
            </p>
          )} */}
          <p className="text-primary font-medium">
            {params.row.workedWithJobsCount}{" "}
            <span className="text-[11px] font-300">(My Company)</span>
          </p>
        </div>
      ),
    },
    {
      field: "certifications",
      headerName: "Certification",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((certificate) => (
          <p className="table-font-size">{certificate.qualificationSubType},</p>
        )),
    },
    {
      field: "equipments",
      headerName: "Equipments",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((equipment) => (
          <p className="table-font-size">{equipment.qualificationSubType}, </p>
        )),
    },
    {
      field: "skills",
      headerName: "Skill Sets",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((skill) => <p className="table-font-size">{skill.subCategory}, </p>),
    },
    {
      field: "rating",
      headerName: "Rating",
      width: 180,
      renderCell: (params) => (
        <div className="flex items-center">
          <BaseRating rating={params.value} />
          <p className="ml-3">{params.value || ""}</p>
        </div>
      ),
    },
    {
      field: "cancellationRate",
      headerName: "Cancellation",
      width: 180,
      renderCell: (params) => <p className="table-font-size">{params.value}%</p>,
    },
    {
      field: "timelinessRate",
      headerName: "Timeliness",
      width: 180,
      renderCell: (params) => <p className="table-font-size">{params.value}%</p>,
    },
    {
      field: "workedWith",
      headerName: "Worked With",
      width: 180,
      renderCell: (params) => (
        <p className="table-font-size">
          {params.value ? (
            <i class="fa fa-check text-sm text-primary bg-[#D8EBF8] rounded-full w-5 text-center"></i>
          ) : (
            <i class="fa fa-xmark text-sm text-red-500 bg-[#FFCDCD] rounded-full w-5 text-center"></i>
          )}
        </p>
      ),
    },
    {
      field: "backgroundChecked",
      headerName: "Background Check",
      width: 180,
      renderCell: (params) => (
        <p className="table-font-size">
          {params.value ? (
            <i class="fa fa-check text-sm text-primary bg-[#D8EBF8] rounded-full w-5 text-center"></i>
          ) : (
            <i class="fa fa-xmark text-sm text-red-500 bg-[#FFCDCD] rounded-full w-5 text-center"></i>
          )}
        </p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Registration Date",
      width: 160,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${dayjs(params.value).format("DD-MM-YYYY") || ""}`}
        </p>
      ),
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      width: 160,
      renderCell: (params) => {
        const raw = params.value;

        if (!raw) return <p>-</p>;

        const phone = parsePhoneNumberFromString(raw.startsWith("+") ? raw : `+${raw}`);

        if (!phone) {
          return <p className="table-font-size cursor-default">{raw}</p>;
        }

        const formatted = phone.formatInternational().replace(/\s+/g, "-");

        // mask after first 10 characters
        const masked = formatted.slice(0, 10) + ".....";

        return <p className="table-font-size cursor-default">{masked}</p>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 240,
      renderCell: (params) => <p className="table-font-size cursor-default">{params.value}</p>,
    },
    {
      field: "personalAddress",
      headerName: "Location",
      width: 220,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.personalAddress?.addressLine1 || ""}`}
        </p>
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.personalAddress?.city || ""}`}
        </p>
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.personalAddress?.state || ""}`}
        </p>
      ),
    },
    {
      field: "zipcode",
      headerName: "Zip / Postal Code",
      width: 150,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.personalAddress?.zipCode || ""}`}
        </p>
      ),
    },
    {
      field: "country",
      headerName: "Country",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size">{params.row?.personalAddress?.country || ""}</p>
      ),
    },

    {
      field: "lastActive",
      headerName: "Last Active",
      width: 140,
      renderCell: (params) => <p className="table-font-size">{dayjs(params.value).fromNow()}</p>,
    },
    {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const menuOptions = [
          ...(hasWorkForceDeleteAccess ? [{ label: "Remove", value: "DELETE" }] : []),
          ,
        ];

        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i class="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(value) => handleItemClick(value, params?.row)}
          />
        );
      },
      pinRight: true,
    },
  ];
  const handleItemClick = (value, params) => {
    if (value === "DELETE") {
      setSelectedEngineer(params);
      setIsDeleteConfirmationModalOpen(true);
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    dispatch(
      setTalentPoolFilters({
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  return (
    <BaseWrapper>
      <div className="bg-white rounded-lg px-5 py-2 shadow-md mt-2">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
              <i className="fa-light fa-filter text-white text-sm"></i>
            </span>
            <p
              className="text-primary ml-2 cursor-pointer"
              onClick={() => setFiltersDrawerOpen(true)}
            >
              Add Filter
            </p>
          </div>
          <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
            <div className="flex gap-2">
              {appliedFilters.map((f) => (
                <div
                  key={(f.key || "") + (f.value || "")}
                  className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                >
                  <span className="text-primary">{f.prefix}:</span>&nbsp;
                  <p className="text-primary text-sm mx-1">{f.value}</p>
                  <button
                    onClick={() => handleRemoveFilter(f.key)}
                    className="ml-2 text-primary hover:text-red-500"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="w-[28%]">
            <BaseTextField
              placeholder="Search by name, email or ID"
              value={search}
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
        </div>
      </div>

      <div className="mt-2">
        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={38}
          columns={columns}
          data={talnetPoolEngineersData?.data || []}
          isLoading={isFetchWorkforceApiLoading}
          disableColumnResize={false}
          disableRowSelectionOnClick
          checkboxSelection
          hideFooter={false}
          pagination
          rowCount={talnetPoolEngineersData?.total}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={pagination}
          onPaginationModelChange={handlePaginationChange}
          paginationMode="server"
        />
      </div>

      {filtersDrawerOpen && (
        <WorkForceFilters
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          fetchWorkforce={() => refetchWorkforce()}
          isLoading={isFetchWorkforceApiLoading}
          setFiltersChanged={setFiltersChanged}
          filterType="talentPool"
        />
      )}

      {isDeleteConfirmationModalOpen && hasWorkForceDeleteAccess && (
        <BaseConfirmModal
          open={isDeleteConfirmationModalOpen}
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          cancelText="Cancel"
          confirmText="Remove"
          confirmButtonColor="error"
          title="Remove Engineer From Talent Pool"
          description={
            <p>
              Are you sure you want to remove
              <span className="text-red-600 font-medium mx-1 capitalize">
                "{selectedEngineer?.name}"
              </span>
              from the Talent Pool? This action cannot be undone.
            </p>
          }
          onConfirm={() => handleRemoveEngineer(selectedEngineer?.id)}
        />
      )}
    </BaseWrapper>
  );
};

export default TalentPoolEngineersTable;
